import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Search, Star, Eye, EyeOff, Pencil, Upload, ImageOff, ImageIcon, Shield, Building2, Palette, FolderUp, CheckCircle2, XCircle, X, Plus } from "lucide-react";
import { formatApiError } from "../lib/format";
import { VENDORS } from "../lib/vendors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const STATUS_COLOR = {
  DRAFT: "bg-zinc-100 text-zinc-700 border-zinc-200",
  NEEDS_REVIEW: "bg-amber-50 text-amber-800 border-amber-200",
  PUBLISHED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ARCHIVED: "bg-rose-50 text-rose-700 border-rose-200",
};

const fmtIDR = (n) => "Rp." + Number(n||0).toLocaleString("id-ID");

export default function MasterProducts() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [imageFilter, setImageFilter] = useState("all"); // all|missing|has
  const [editItem, setEditItem] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [cms, setCms] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master-products");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat"); }
    finally { setLoading(false); }
  };
  const loadCms = async () => {
    try {
      const { data } = await api.get("/cms/landing");
      setCms(data || null);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); if (isOwner) loadCms(); }, [isOwner]);

  const families = useMemo(() => Array.from(new Set(rows.map(r => r.product_family))).sort(), [rows]);
  const filtered = useMemo(() => rows.filter(r => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (familyFilter !== "all" && r.product_family !== familyFilter) return false;
    if (imageFilter === "missing" && r.card_image) return false;
    if (imageFilter === "has" && !r.card_image) return false;
    if (q) {
      const s = `${r.product_id} ${r.product_name} ${r.brand}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, q, statusFilter, familyFilter, imageFilter]);

  const togglePublish = async (p) => {
    try {
      if (p.status === "PUBLISHED") {
        await api.post(`/master-products/${p.product_id}/unpublish`);
        toast.success("Dihapus dari public");
      } else {
        await api.post(`/master-products/${p.product_id}/publish`);
        toast.success("Publish ke public");
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleFeatured = async (p) => {
    try {
      await api.patch(`/master-products/${p.product_id}`, { featured: !p.featured });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stats = useMemo(() => {
    const s = { total: rows.length, published: 0, review: 0, draft: 0, no_image: 0 };
    rows.forEach(r => {
      if (r.status === "PUBLISHED") s.published++;
      else if (r.status === "NEEDS_REVIEW") s.review++;
      else s.draft++;
      if (!r.card_image) s.no_image++;
    });
    return s;
  }, [rows]);

  const orphanCount = useMemo(() => rows.filter(r =>
    r.status === "PUBLISHED" && (r.client_visible === false || r.active === false)
  ).length, [rows]);

  const fixVisibility = async () => {
    if (!isOwner) return;
    if (orphanCount === 0) { toast.info("Tidak ada paket orphan yang perlu diperbaiki"); return; }
    try {
      const { data } = await api.post("/master-products/fix-visibility");
      toast.success(`${data.fixed_count} paket berhasil ditampilkan di landing. Total visible: ${data.now_visible}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal memperbaiki visibility"); }
  };

  return (
    <div className="space-y-6" data-testid="master-products-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">Portfolio & Master Pricelist</div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-semibold">Kelola Paket & Foto Portofolio</h1>
            <p className="text-xs text-[#5C6066] mt-1">
              Upload & ganti foto per paket, foto hero per vendor, dan logo brand. {isOwner ? "Sebagai Owner, Anda memiliki akses penuh." : <span className="text-rose-600 font-medium">Hanya Owner yang dapat mengubah foto/logo.</span>}
            </p>
          </div>
          {isOwner && (
            <div className="flex flex-wrap gap-2">
              {orphanCount > 0 && (
                <Button onClick={fixVisibility} variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50 h-9" data-testid="mp-fix-visibility-btn">
                  <Eye className="w-4 h-4 mr-1.5"/> Perbaiki {orphanCount} Paket Hilang dari Landing
                </Button>
              )}
              <Button onClick={()=>setAddOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white h-9" data-testid="mp-add-btn">
                <Plus className="w-4 h-4 mr-1.5"/> Tambah Paket Baru
              </Button>
              <Button onClick={()=>setBatchOpen(true)} className="bg-[#8B5A2A] hover:bg-[#1E1611] text-white h-9" data-testid="mp-batch-btn">
                <FolderUp className="w-4 h-4 mr-1.5"/> Batch Upload Foto
              </Button>
            </div>
          )}
        </div>
      </div>

      {batchOpen && <BatchUploadModal rows={rows} onClose={()=>{ setBatchOpen(false); load(); }}/>}

      {/* Owner-only: Branding & Vendor Hero */}
      {isOwner && cms && (
        <BrandingCard cms={cms} reload={loadCms} />
      )}
      {isOwner && cms && (
        <VendorHeroCard cms={cms} reload={loadCms} />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total, color: "bg-white" },
          { label: "Published", value: stats.published, color: "bg-emerald-50" },
          { label: "Needs Review", value: stats.review, color: "bg-amber-50" },
          { label: "Draft", value: stats.draft, color: "bg-zinc-50" },
          { label: "Belum Ada Foto", value: stats.no_image, color: "bg-rose-50" },
        ].map(s => (
          <Card key={s.label} className={`p-4 ${s.color} border-[#E5E0D8]`}>
            <div className="text-[10px] uppercase tracking-wide text-[#8C9096] font-semibold">{s.label}</div>
            <div className="text-2xl font-mono font-semibold">{s.value}</div>
          </Card>
        ))}
      </div>

      <Card className="p-4 border-[#E5E0D8] flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Cari</Label>
          <div className="relative mt-1.5">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="ID / nama / brand" className="pl-9 h-10 bg-white" data-testid="mp-search"/>
          </div>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Foto</Label>
          <Select value={imageFilter} onValueChange={setImageFilter}>
            <SelectTrigger className="mt-1.5 h-10 bg-white" data-testid="mp-image-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="missing">Belum Ada</SelectItem>
              <SelectItem value="has">Sudah Ada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="mt-1.5 h-10 bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="PUBLISHED">Published</SelectItem>
              <SelectItem value="NEEDS_REVIEW">Needs Review</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="ARCHIVED">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Family</Label>
          <Select value={familyFilter} onValueChange={setFamilyFilter}>
            <SelectTrigger className="mt-1.5 h-10 bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Family</SelectItem>
              {families.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden border-[#E5E0D8]">
        {loading ? <div className="p-8 text-center text-[#8C9096]"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Memuat...</div>
         : filtered.length === 0 ? <div className="p-10 text-center text-[#8C9096] text-sm">Tidak ada produk cocok</div>
         : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[10px] uppercase tracking-widest text-[#8C9096]">
                <tr>
                  <th className="text-left p-3 w-20">Foto</th>
                  <th className="text-left p-3">Produk</th>
                  <th className="text-left p-3">Family / Kategori</th>
                  <th className="text-left p-3">Harga</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.product_id} className="border-t border-[#EFE9DE] hover:bg-[#FAF6EE]/50" data-testid={`mp-row-${p.product_id}`}>
                    <td className="p-3">
                      {p.card_image ? (
                        <img src={resolveUrl(p.card_image)} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#E5E0D8]"
                          data-testid={`mp-thumb-${p.product_id}`}/>
                      ) : (
                        <div className="w-14 h-14 rounded-lg border border-dashed border-rose-300 bg-rose-50 flex items-center justify-center text-rose-500"
                          data-testid={`mp-no-image-${p.product_id}`}>
                          <ImageOff className="w-5 h-5"/>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-mono text-[10px] text-[#8C9096]">{p.product_id}</div>
                      <div className="font-medium flex items-center gap-1.5">
                        {p.product_name}
                        {p.featured && <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500"/>}
                      </div>
                      <div className="text-[11px] text-[#8C9096]">{p.brand}</div>
                    </td>
                    <td className="p-3 text-xs">
                      <div>{p.product_family}</div>
                      <div className="text-[#8C9096]">{p.event_category}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-mono">{fmtIDR(p.normal_price)}</div>
                      {p.promo_price > 0 && (
                        <div className={`text-[10px] font-mono ${p.promo_active ? "text-emerald-700" : "text-[#8C9096] line-through"}`}>
                          Promo: {fmtIDR(p.promo_price)}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge className={`${STATUS_COLOR[p.status]} text-[10px] font-mono border`}>{p.status}</Badge>
                      {p.status === "PUBLISHED" && !p.client_visible && (
                        <div className="text-[10px] text-rose-700 mt-1">tidak visible</div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => toggleFeatured(p)} title="Toggle Featured" data-testid={`mp-featured-${p.product_id}`}>
                          <Star className={`w-3.5 h-3.5 ${p.featured ? "fill-amber-500 text-amber-500" : "text-[#8C9096]"}`}/>
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => togglePublish(p)} title={p.status === "PUBLISHED" ? "Unpublish" : "Publish"} data-testid={`mp-publish-${p.product_id}`}>
                          {p.status === "PUBLISHED" ? <EyeOff className="w-3.5 h-3.5 text-rose-600"/> : <Eye className="w-3.5 h-3.5 text-emerald-700"/>}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditItem(p)} data-testid={`mp-edit-${p.product_id}`}>
                          <Pencil className="w-3.5 h-3.5"/>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editItem && <ProductEdit item={editItem} isOwner={isOwner} onClose={()=>{ setEditItem(null); load(); }}/>}
      {addOpen && <ProductAdd onClose={()=>{ setAddOpen(false); load(); }}/>}
    </div>
  );
}

/* ------------------ BRANDING (Logo) ------------------ */
function BrandingCard({ cms, reload }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const logoUrl = cms.logo_image_id ? `${BACKEND_URL}/api/public/cms/gallery/${cms.logo_image_id}` : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_category", "Other");
      fd.append("label", "Logo DWG");
      const { data } = await api.post("/cms/landing/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const media_id = data.id;
      await api.put("/cms/landing?mode=publish", { logo_image_id: media_id });
      toast.success("Logo diperbarui");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload"); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!confirm("Hapus logo custom (kembali ke default)?")) return;
    setBusy(true);
    try {
      await api.put("/cms/landing?mode=publish", { logo_image_id: null });
      toast.success("Kembali ke logo default");
      reload();
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-[#E5E0D8] bg-white" data-testid="branding-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#8B5A2A]/10 flex items-center justify-center text-[#8B5A2A]">
            <Palette className="w-5 h-5"/>
          </div>
          <div>
            <h3 className="font-semibold text-base">Logo Brand</h3>
            <p className="text-xs text-[#5C6066]">Muncul di header semua halaman publik. Rekomendasi PNG transparan, 512×512.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-16 h-16 rounded-full border border-[#E5E0D8] object-cover bg-[#F7F1E8]" data-testid="branding-logo-preview"/>
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C4A57B] to-[#8B5A2A] flex items-center justify-center text-white text-xs uppercase tracking-wider">
              Default
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e)=>upload(e.target.files?.[0])} data-testid="branding-file"/>
          <Button size="sm" onClick={()=>fileRef.current?.click()} disabled={busy} className="bg-[#8B5A2A] hover:bg-[#1E1611] text-white" data-testid="branding-upload">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Upload className="w-4 h-4 mr-1.5"/>}
            {logoUrl ? "Ganti Logo" : "Upload Logo"}
          </Button>
          {logoUrl && (
            <Button size="sm" variant="outline" onClick={clear} disabled={busy} data-testid="branding-clear">Kembali ke Default</Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ------------------ VENDOR HERO IMAGES ------------------ */
function VendorHeroCard({ cms, reload }) {
  const [openSlug, setOpenSlug] = useState(null);
  const [busy, setBusy] = useState("");
  const inputs = useRef({});

  const vendorImage = (slug) => {
    const v = (cms.vendor_images || {})[slug];
    return v?.media_id ? `${BACKEND_URL}/api/public/cms/gallery/${v.media_id}` : null;
  };

  const upload = async (slug, file) => {
    if (!file) return;
    setBusy(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_category", "Other");
      fd.append("label", `Vendor Hero — ${slug}`);
      const { data } = await api.post("/cms/landing/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const media_id = data.id;
      const next = { ...(cms.vendor_images || {}), [slug]: { media_id } };
      await api.put("/cms/landing?mode=publish", { vendor_images: next });
      toast.success(`Foto vendor ${slug} diperbarui`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload"); }
    finally { setBusy(""); }
  };

  const clear = async (slug) => {
    if (!confirm(`Hapus foto vendor ${slug}?`)) return;
    setBusy(slug);
    try {
      const next = { ...(cms.vendor_images || {}) };
      delete next[slug];
      await api.put("/cms/landing?mode=publish", { vendor_images: next });
      toast.success("Foto vendor dihapus");
      reload();
    } finally { setBusy(""); }
  };

  return (
    <Card className="p-5 border-[#E5E0D8] bg-white" data-testid="vendor-hero-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#8B5A2A]/10 flex items-center justify-center text-[#8B5A2A]">
          <Building2 className="w-5 h-5"/>
        </div>
        <div>
          <h3 className="font-semibold text-base">Foto Hero Vendor</h3>
          <p className="text-xs text-[#5C6066]">Foto yang muncul di kartu vendor & halaman `/vendor/:slug`. Rekomendasi landscape 1600×900.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {VENDORS.map((v) => {
          const url = vendorImage(v.slug);
          return (
            <div key={v.slug} className="rounded-xl border border-[#E5E0D8] overflow-hidden bg-white" data-testid={`vhero-${v.slug}`}>
              <div className="relative h-32 bg-[#F7F1E8]">
                <img src={url || v.image} alt={v.name} className="w-full h-full object-cover"/>
                {url && <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-emerald-600 text-white px-2 py-0.5 rounded-full">Custom</span>}
              </div>
              <div className="p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">{v.tagline}</div>
                <div className="font-medium text-sm">{v.name}</div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <input ref={(el)=>{inputs.current[v.slug] = el;}} type="file" accept="image/*" className="hidden" onChange={(e)=>upload(v.slug, e.target.files?.[0])} data-testid={`vhero-file-${v.slug}`}/>
                  <Button size="sm" onClick={()=>inputs.current[v.slug]?.click()} disabled={busy===v.slug}
                    className="bg-[#8B5A2A] hover:bg-[#1E1611] text-white text-[11px]" data-testid={`vhero-upload-${v.slug}`}>
                    {busy===v.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1"/> : <Upload className="w-3.5 h-3.5 mr-1"/>}
                    {url ? "Ganti" : "Upload"}
                  </Button>
                  {url && (
                    <Button size="sm" variant="outline" onClick={()=>clear(v.slug)} disabled={busy===v.slug} className="text-[11px]" data-testid={`vhero-clear-${v.slug}`}>
                      Hapus
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ------------------ PRODUCT EDIT ------------------ */
function ProductEdit({ item, isOwner, onClose }) {
  const [form, setForm] = useState({
    product_name: item.product_name || "",
    brand: item.brand || "",
    event_category: item.event_category || "",
    normal_price: item.normal_price || 0,
    promo_price: item.promo_price || 0,
    promo_start_date: item.promo_start_date || "",
    promo_end_date: item.promo_end_date || "",
    short_description: item.short_description || "",
    included_items: (item.included_items || []).join("\n"),
    important_notes: (item.important_notes || []).join("\n"),
    terms_and_conditions: (item.terms_and_conditions || []).join("\n"),
    guest_capacity: item.guest_capacity || 0,
    crew_count: item.crew_count || 0,
    card_image: item.card_image || "",
    detail_image: item.detail_image || "",
    status: item.status,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const cardFileRef = useRef(null);
  const detailFileRef = useRef(null);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        normal_price: Number(form.normal_price),
        promo_price: Number(form.promo_price),
        guest_capacity: Number(form.guest_capacity),
        crew_count: Number(form.crew_count),
        included_items: form.included_items.split("\n").map(s=>s.trim()).filter(Boolean),
        important_notes: form.important_notes.split("\n").map(s=>s.trim()).filter(Boolean),
        terms_and_conditions: form.terms_and_conditions.split("\n").map(s=>s.trim()).filter(Boolean),
      };
      await api.patch(`/master-products/${item.product_id}`, payload);
      toast.success("Produk disimpan");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };

  const uploadImg = async (image_type, file) => {
    if (!file) return;
    if (!isOwner) { toast.error("Hanya Owner yang dapat upload foto"); return; }
    setUploading(image_type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_type", image_type);
      const { data } = await api.post(`/master-products/${item.product_id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const field = data.field;
      setForm(f => ({ ...f, [field]: data.url }));
      toast.success(`Foto ${image_type === "card" ? "kartu" : "detail"} berhasil diupload`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload"); }
    finally { setUploading(""); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mp-edit-modal">
        <DialogHeader>
          <DialogTitle>Edit: {item.product_id}</DialogTitle>
          <DialogDescription className="text-xs text-[#5C6066]">Ubah data paket & unggah foto portofolio (khusus Owner).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">

          {/* Portfolio Images Section */}
          <Card className="p-4 bg-[#FAF6EE] border-[#E5E0D8]">
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon className="w-4 h-4 text-[#8B5A2A]"/>
              <div className="font-semibold text-sm">Foto Portofolio</div>
              {!isOwner && <Badge variant="outline" className="text-[9px] text-rose-700 border-rose-300 bg-rose-50"><Shield className="w-2.5 h-2.5 mr-1"/>Owner only</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ImageSlot
                label="Foto Kartu (list)"
                url={form.card_image}
                onUpload={(f)=>uploadImg("card", f)}
                busy={uploading==="card"}
                disabled={!isOwner}
                inputRef={cardFileRef}
                testid="mp-upload-card"
              />
              <ImageSlot
                label="Foto Detail (halaman)"
                url={form.detail_image}
                onUpload={(f)=>uploadImg("detail", f)}
                busy={uploading==="detail"}
                disabled={!isOwner}
                inputRef={detailFileRef}
                testid="mp-upload-detail"
              />
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Nama Produk</Label>
              <Input className="mt-1.5 bg-white" value={form.product_name} onChange={(e)=>setForm({...form, product_name: e.target.value})} data-testid="mp-form-name"/></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Brand / Vendor</Label>
              <Select value={form.brand || ""} onValueChange={(v)=>setForm({...form, brand: v})}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="mp-form-brand-select"><SelectValue placeholder="Pilih vendor…"/></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="DWG All-in">DWG All-in (Bundle Multi-Vendor)</SelectItem>
                  {VENDORS.map((v) => <SelectItem key={v.slug} value={v.name}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Event Category</Label>
              <Input className="mt-1.5 bg-white" value={form.event_category} onChange={(e)=>setForm({...form, event_category: e.target.value})}/></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Harga Normal (Rp)</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.normal_price} onChange={(e)=>setForm({...form, normal_price: e.target.value})} data-testid="mp-form-price"/></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Promo (Rp)</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.promo_price} onChange={(e)=>setForm({...form, promo_price: e.target.value})}/></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Promo Start</Label>
              <Input type="date" className="mt-1.5 bg-white" value={form.promo_start_date} onChange={(e)=>setForm({...form, promo_start_date: e.target.value})}/></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Promo End</Label>
              <Input type="date" className="mt-1.5 bg-white" value={form.promo_end_date} onChange={(e)=>setForm({...form, promo_end_date: e.target.value})}/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Kapasitas Tamu</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.guest_capacity} onChange={(e)=>setForm({...form, guest_capacity: e.target.value})}/></div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Crew Count</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.crew_count} onChange={(e)=>setForm({...form, crew_count: e.target.value})}/></div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Deskripsi Singkat</Label>
            <Input className="mt-1.5 bg-white" value={form.short_description} onChange={(e)=>setForm({...form, short_description: e.target.value})}/></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Included Items (satu per baris)</Label>
            <Textarea rows={6} className="mt-1.5 bg-white text-xs" value={form.included_items} onChange={(e)=>setForm({...form, included_items: e.target.value})} data-testid="mp-form-included"/></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Important Notes (satu per baris)</Label>
            <Textarea rows={3} className="mt-1.5 bg-white text-xs" value={form.important_notes} onChange={(e)=>setForm({...form, important_notes: e.target.value})}/></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Terms & Conditions (satu per baris)</Label>
            <Textarea rows={4} className="mt-1.5 bg-white text-xs" value={form.terms_and_conditions} onChange={(e)=>setForm({...form, terms_and_conditions: e.target.value})}/></div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Status</Label>
            <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
              <SelectTrigger className="mt-1.5 bg-white"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="NEEDS_REVIEW">Needs Review</SelectItem>
                <SelectItem value="PUBLISHED">Published</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#8B262D] hover:bg-[#6E1C22] text-white" data-testid="mp-form-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : null}Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImageSlot({ label, url, onUpload, busy, disabled, inputRef, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">{label}</Label>
      <div className="mt-1.5 border border-[#E5E0D8] rounded-lg overflow-hidden bg-white">
        {url ? (
          <img src={resolveUrl(url)} alt={label} className="w-full h-32 object-cover"/>
        ) : (
          <div className="w-full h-32 flex flex-col items-center justify-center text-[#8C9096] bg-[#FAF6EE]">
            <ImageOff className="w-6 h-6"/>
            <div className="text-[10px] uppercase tracking-widest mt-1">Belum ada foto</div>
          </div>
        )}
        <div className="p-2 flex gap-2 items-center border-t border-[#E5E0D8]">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e)=>onUpload(e.target.files?.[0])} data-testid={`${testid}-file`}/>
          <Button size="sm" onClick={()=>inputRef.current?.click()} disabled={busy || disabled}
            className="bg-[#8B5A2A] hover:bg-[#1E1611] text-white text-[11px] flex-1" data-testid={testid}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1"/> : <Upload className="w-3.5 h-3.5 mr-1"/>}
            {url ? "Ganti Foto" : "Upload Foto"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------ PRODUCT ADD (NEW) ------------------ */
function ProductAdd({ onClose }) {
  const [form, setForm] = useState({
    product_id: "", product_name: "", brand: "", product_family: "",
    event_category: "Wedding", normal_price: 0, promo_price: 0,
    guest_capacity: 0, crew_count: 0,
    short_description: "", included_items: "",
  });
  const [busy, setBusy] = useState(false);

  // Auto-generate product_id dari nama (kebab-case) kalau user belum isi manual
  const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const setName = (v) => {
    setForm((f) => ({
      ...f, product_name: v,
      product_id: f.product_id && f.product_id !== slug(f.product_name) ? f.product_id : slug(v),
    }));
  };

  const submit = async () => {
    if (!form.product_id || !form.product_name || !form.product_family) {
      toast.error("Nama, ID, dan Family/Kategori wajib diisi"); return;
    }
    if (!form.brand) { toast.error("Pilih Brand/Vendor dari dropdown"); return; }
    setBusy(true);
    try {
      const payload = {
        product_id: form.product_id,
        product_name: form.product_name,
        product_family: form.product_family.toUpperCase(),
        brand: form.brand,
        event_category: form.event_category,
        normal_price: Number(form.normal_price),
        promo_price: Number(form.promo_price),
        guest_capacity: Number(form.guest_capacity),
        crew_count: Number(form.crew_count),
        short_description: form.short_description,
        included_items: form.included_items.split("\n").map(s=>s.trim()).filter(Boolean),
      };
      await api.post("/master-products", payload);
      toast.success(`Paket "${form.product_name}" berhasil ditambahkan (status: DRAFT). Edit untuk lengkapi detail & publish.`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal menambah paket"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mp-add-modal">
        <DialogHeader>
          <DialogTitle>Tambah Paket Baru</DialogTitle>
          <DialogDescription className="text-xs text-[#5C6066]">
            Isi data dasar paket. Status default: DRAFT. Setelah tersimpan, klik Edit untuk lengkapi foto, terms, dan publish ke landing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Nama Paket *</Label>
              <Input className="mt-1.5 bg-white" value={form.product_name} onChange={(e)=>setName(e.target.value)} placeholder="Contoh: Intimade Basic" data-testid="mp-add-name"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Product ID *</Label>
              <Input className="mt-1.5 bg-white font-mono text-xs" value={form.product_id} onChange={(e)=>setForm({...form, product_id: e.target.value.trim()})} placeholder="auto dari nama" data-testid="mp-add-id"/>
              <div className="text-[10px] text-[#8C9096] mt-1">Auto dari nama · unik · gunakan huruf kecil & tanda hubung</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Brand / Vendor *</Label>
              <Select value={form.brand} onValueChange={(v)=>setForm({...form, brand: v})}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="mp-add-brand"><SelectValue placeholder="Pilih vendor…"/></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="DWG All-in">DWG All-in (Bundle Multi-Vendor)</SelectItem>
                  {VENDORS.map((v) => <SelectItem key={v.slug} value={v.name}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Family / Kategori *</Label>
              <Input className="mt-1.5 bg-white uppercase" value={form.product_family} onChange={(e)=>setForm({...form, product_family: e.target.value})} placeholder="INTIMADE / INDOOR WEDDING / dst" data-testid="mp-add-family"/>
              <div className="text-[10px] text-[#8C9096] mt-1">Family DWG bundling: INTIMADE, ESSENTIAL WEDDING, INDOOR/OUTDOOR WEDDING, AKAD PACKAGE, dst.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Event Category</Label>
              <Select value={form.event_category} onValueChange={(v)=>setForm({...form, event_category: v})}>
                <SelectTrigger className="mt-1.5 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wedding">Wedding</SelectItem>
                  <SelectItem value="Akad">Akad</SelectItem>
                  <SelectItem value="Khitan">Khitan</SelectItem>
                  <SelectItem value="Prewedding">Prewedding</SelectItem>
                  <SelectItem value="Family">Family</SelectItem>
                  <SelectItem value="Maternity">Maternity</SelectItem>
                  <SelectItem value="Graduation">Graduation</SelectItem>
                  <SelectItem value="Event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Harga Normal (Rp) *</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.normal_price} onChange={(e)=>setForm({...form, normal_price: e.target.value})} data-testid="mp-add-price"/>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Promo (Rp)</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.promo_price} onChange={(e)=>setForm({...form, promo_price: e.target.value})}/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Kapasitas Tamu</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.guest_capacity} onChange={(e)=>setForm({...form, guest_capacity: e.target.value})}/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Crew Count</Label>
              <Input type="number" className="mt-1.5 bg-white font-mono" value={form.crew_count} onChange={(e)=>setForm({...form, crew_count: e.target.value})}/>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Deskripsi Singkat</Label>
            <Input className="mt-1.5 bg-white" value={form.short_description} onChange={(e)=>setForm({...form, short_description: e.target.value})} placeholder="1–2 kalimat untuk kartu paket"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Included Items (satu per baris)</Label>
            <Textarea rows={5} className="mt-1.5 bg-white text-xs" value={form.included_items} onChange={(e)=>setForm({...form, included_items: e.target.value})} placeholder="Make Up Pengantin (1 Kali Ganti)&#10;Dekorasi Simple&#10;Foto & Video Wedding"/>
          </div>
          <div className="p-3 rounded-md bg-[#FBF5E5] border border-[#E4D9BE] text-[11px] text-[#7A5F1A]">
            📝 <b>Info:</b> Setelah tersimpan, status paket <b>DRAFT</b>. Buka <i>Edit</i> untuk upload foto kartu/detail, tambahkan terms, promo, dst — lalu ubah status ke <b>PUBLISHED</b> untuk tampil di landing.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mp-add-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Plus className="w-4 h-4 mr-1.5"/>}Simpan Paket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------ BATCH UPLOAD MODAL ------------------ */
function BatchUploadModal({ rows, onClose }) {
  const [items, setItems] = useState([]);  // {file, preview, product_id, image_type, status, error}
  const [busy, setBusy] = useState(false);
  const dropRef = useRef(null);

  // Fuzzy match filename → product suggestion
  const suggestProduct = (filename) => {
    const base = filename.toLowerCase().replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
    let best = null; let bestScore = 0;
    for (const r of rows) {
      const name = (r.product_name || "").toLowerCase();
      const id = (r.product_id || "").toLowerCase();
      let score = 0;
      name.split(" ").forEach(tok => { if (tok.length > 2 && base.includes(tok)) score += tok.length; });
      if (base.includes(id)) score += 20;
      if (score > bestScore) { bestScore = score; best = r; }
    }
    return bestScore >= 4 ? best?.product_id : "";
  };

  const addFiles = (fileList) => {
    const arr = Array.from(fileList).filter(f => f.type.startsWith("image/"));
    const next = arr.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      product_id: suggestProduct(f.name),
      image_type: "card",
      status: "pending",
      error: "",
    }));
    setItems(prev => [...prev, ...next]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const uploadAll = async () => {
    const validItems = items.filter(it => it.product_id && it.status !== "success");
    if (validItems.length === 0) { toast.error("Isi pilihan produk untuk setiap foto"); return; }
    setBusy(true);
    // Upload in parallel batches of 4 to avoid overwhelming the browser/server
    const CONCURRENCY = 4;
    const queue = [...validItems];
    const results = [];
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length) {
        const it = queue.shift();
        const idx = items.indexOf(it);
        setItems(prev => prev.map((x,i) => i===idx ? {...x, status:"uploading"} : x));
        try {
          const fd = new FormData();
          fd.append("file", it.file);
          fd.append("image_type", it.image_type);
          await api.post(`/master-products/${it.product_id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setItems(prev => prev.map((x,i) => i===idx ? {...x, status:"success"} : x));
          results.push({ ok: true });
        } catch (e) {
          setItems(prev => prev.map((x,i) => i===idx ? {...x, status:"error", error: formatApiError(e.response?.data?.detail) || "Gagal"} : x));
          results.push({ ok: false });
        }
      }
    });
    await Promise.all(workers);
    const okCount = results.filter(r => r.ok).length;
    const failCount = results.length - okCount;
    if (okCount > 0) toast.success(`${okCount} foto berhasil di-upload`);
    if (failCount > 0) toast.error(`${failCount} foto gagal — cek daftar`);
    setBusy(false);
  };

  const remove = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems(prev => prev.map((x,i) => i===idx ? {...x, ...patch} : x));

  const pendingCount = items.filter(x => x.status === "pending").length;
  const successCount = items.filter(x => x.status === "success").length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="batch-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FolderUp className="w-5 h-5 text-[#8B5A2A]"/> Batch Upload Foto Portofolio</DialogTitle>
          <DialogDescription className="text-xs text-[#5C6066]">
            Drag-and-drop atau pilih beberapa foto sekaligus. Sistem akan mencoba mendeteksi paket berdasarkan nama file. Semua foto otomatis di-resize & dikonversi ke WebP untuk kecepatan.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={dropRef}
          onDragOver={(e)=>e.preventDefault()}
          onDrop={onDrop}
          className="mt-4 rounded-xl border-2 border-dashed border-[#C4A57B] bg-[#FAF6EE] p-8 text-center cursor-pointer hover:bg-[#F5EAD1] transition-colors"
          onClick={()=>document.getElementById("batch-files-input")?.click()}
          data-testid="batch-dropzone"
        >
          <FolderUp className="w-8 h-8 mx-auto text-[#8B5A2A] mb-2"/>
          <div className="font-semibold text-sm">Klik atau drop foto di sini</div>
          <div className="text-[11px] text-[#5C6066] mt-1">JPG / PNG / WEBP (max 8MB per foto). Otomatis di-resize 1600px & konversi WebP.</div>
          <input id="batch-files-input" type="file" accept="image/*" multiple className="hidden" onChange={(e)=>addFiles(e.target.files)} data-testid="batch-files-input"/>
        </div>

        {items.length > 0 && (
          <div className="mt-4 space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="batch-list">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 border border-[#E5E0D8] rounded-lg bg-white" data-testid={`batch-item-${idx}`}>
                <img src={it.preview} alt="" className="w-14 h-14 rounded object-cover shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono text-[#5C6066] truncate">{it.file.name}</div>
                  <div className="text-[10px] text-[#8C9096]">{(it.file.size / 1024).toFixed(0)} KB</div>
                </div>
                <Select value={it.product_id} onValueChange={(v)=>updateItem(idx, {product_id: v})}>
                  <SelectTrigger className="w-52 h-9 text-xs bg-white"><SelectValue placeholder="Pilih paket..."/></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {rows.map(r => <SelectItem key={r.product_id} value={r.product_id} className="text-xs">{r.product_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={it.image_type} onValueChange={(v)=>updateItem(idx, {image_type: v})}>
                  <SelectTrigger className="w-28 h-9 text-xs bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card" className="text-xs">Kartu</SelectItem>
                    <SelectItem value="detail" className="text-xs">Detail</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-20 text-center">
                  {it.status === "success" && <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" data-testid={`batch-ok-${idx}`}/>}
                  {it.status === "uploading" && <Loader2 className="w-5 h-5 animate-spin text-[#8B5A2A] mx-auto"/>}
                  {it.status === "error" && <XCircle className="w-5 h-5 text-rose-600 mx-auto" title={it.error} data-testid={`batch-err-${idx}`}/>}
                  {it.status === "pending" && <span className="text-[10px] text-[#8C9096]">Siap</span>}
                </div>
                <Button size="sm" variant="ghost" onClick={()=>remove(idx)} disabled={busy}><X className="w-3.5 h-3.5"/></Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-4 flex-wrap gap-2">
          <div className="text-xs text-[#5C6066] flex-1">
            {items.length} foto · {successCount} berhasil · {pendingCount} pending
          </div>
          <Button variant="outline" onClick={onClose} disabled={busy}>Tutup</Button>
          <Button onClick={uploadAll} disabled={busy || items.length === 0}
            className="bg-[#8B5A2A] hover:bg-[#1E1611] text-white" data-testid="batch-upload-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Upload className="w-4 h-4 mr-1.5"/>}
            Upload Semua
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


